import moment from "moment";
import * as R from "ramda";
import uuid from "uuid/v1";
import { Twix } from "./Twix";
import { initConfig } from "./apiConfig";
import { getLocalStorageInfo, now } from "../../utils";
import * as Promise from "bluebird";
Promise.config({ warnings: false, longStackTraces: false, asyncHooks: false });

const stringify = (data, prefix) => {
  const obj = JSON.parse(JSON.stringify(data));

  const serialize = R.mapObjIndexed((v, p) => {
    const k = prefix ? prefix + "[" + p + "]" : p;
    return v !== null && typeof v === "object"
      ? stringify(v, k)
      : encodeURIComponent(k) + "=" + encodeURIComponent(v);
  });

  return R.pipe(serialize, R.values, R.join("&"))(obj);
};

const sessionHandler = {
  accessToken: null,
  userData: null,
  checkOfflineOn: false,
  expireAt: 0,
  statusLastCallTime: 0,
  lastStatus: true,
  forceLocalSync: false,
};

const apiInit = (apiUrl) => (globalOptions) => {
  if (!apiUrl) {
    throw "An API url has to be set";
  }

  const workerPool = {};
  const apiOptions = R.merge(
    {
      apiActions: {
        connectionStatus: (status) =>
          console.log("api connection status", status),
      },
      fixedParams: {
        time_offset: new Date().getTimezoneOffset() * 60 * 1000,
      },
      fixedData: {},
    },
    globalOptions,
  );

  const addFixedParam = (paramName, paramValue) => {
    apiOptions.fixedParams[paramName] = paramValue;
  };

  const addFixedData = (paramName, paramValue) => {
    apiOptions.fixedData[paramName] = paramValue;
  };

  const ajax = (path, options, callback) =>
    new Promise((success, fail) => {
      const requestUUID = uuid();
      const isGetType =
        options.type != "POST" &&
        options.type != "PUT" &&
        options.type != "DELETE";
      // const isGetType = options.type != 'POST' && options.type != 'DELETE';

      const params = R.pipe(
        R.merge(apiOptions.fixedParams),
        (params) => (isGetType ? R.merge(params)(options.data || {}) : params),
        stringify,
      )(options.params);

      const requestUrl =
        (options.url ? options.url : apiUrl) + path + "?" + params;
      const data = options.processData
        ? R.merge(apiOptions.fixedData, !isGetType ? options.data : {})
        : options.data || {};
      // console.log("loading " + requestUrl);
      // console.log('data:', data);
      // console.log('proccess data:', options.processData);

      const ajaxOptions = R.pipe(
        R.merge(R.omit("noWorker")(options)),
        R.omit("processData"),
        R.merge({ type: "GET", uuid: requestUUID }),
      )(
        // options.noWorker?
        {
          url: requestUrl,
          success,
          error: (statusCode, statusText, errorThrown) => {
            if (statusCode === 401 && sessionHandler.accessToken) {
              localStorage.removeItem("access_token");
              localStorage.removeItem("refresh_token");
              window.location.reload();
              return;
            }
            fail(errorThrown);
          },
          data:
            !isGetType && options.processData !== false
              ? JSON.stringify(data)
              : data,
          headers: !isGetType
            ? R.merge({
                "content-type": "application/json",
              })(options.headers)
            : options.headers,
        },
        // : {
        //     url: requestUrl,
        //     crossDomain: true,
        //     data:
        //       !isGetType && options.processData !== false
        //         ? JSON.stringify(data)
        //         : data,
        //     headers: !isGetType
        //       ? R.merge({
        //           "content-type": "application/json",
        //         })(options.headers)
        //       : options.headers,
        //   }
      );

      // if (options.noWorker) {
      if (true) {
        const twixClient = Twix.ajax(ajaxOptions);
        if (callback) {
          callback(twixClient);
        }
      } else {
        workerPool[requestUUID] = new Worker("/api/apiWorker.js");

        workerPool[requestUUID].onmessage = (event) => {
          const response = event.data;
          // console.log('response', response);
          workerPool[response.uuid].terminate();

          if (response.type == "success") {
            const data = response.data;
            success(data);
          } else if (response.type == "error") {
            if (response.errorThrown === 401) {
              fail("Session expired");
            } else {
              fail(response.errorThrown);
            }
          }
        };

        // console.log('request', ajaxOptions);
        workerPool[requestUUID].postMessage(ajaxOptions);
      }
    });

  const getApiUrl = (path, optional_params) => {
    const params = R.pipe(
      R.merge(apiOptions.fixedParams),
      stringify,
    )(optional_params || {});

    // console.log("params:", params);

    return apiUrl + path + "?" + params;
  };

  const setAccessToken = ({ access_token, user_data, checkOfflineOn }) => {
    sessionHandler.accessToken = access_token;
    sessionHandler.userData = user_data;
    sessionHandler.checkOfflineOn = checkOfflineOn || true;
    if (access_token && access_token.expires_in) {
      addFixedParam("access_token", access_token.access_token);
      sessionHandler.expireAt = now() + access_token.expires_in * 1000;
    } else {
      sessionHandler.expireAt = 0;
    }
    // console.log(sessionHandler);
  };

  const checkAccessToken = () =>
    new Promise((success, fail) => {
      const DAY_SPAN = 60 * 60 * 24 * 1000;

      // console.log("Checking session");
      // console.log("Session expires At", sessionHandler.expireAt);
      // console.log("now", now());
      // console.log("check", now() - sessionHandler.expireAt < DAY_SPAN);

      if (now() > sessionHandler.expireAt) {
        // console.log("Session expired");
        if (
          sessionHandler.accessToken &&
          now() - sessionHandler.expireAt < DAY_SPAN
        ) {
          // console.log("Refreshing session");
          ajax(
            `user/refreshToken/${sessionHandler.accessToken.refresh_token}.json`,
            { type: "GET" },
          )
            .then((access_token) => {
              sessionHandler.accessToken = access_token;
              addFixedParam("access_token", access_token.access_token);
              success();
            })
            .catch(() => {
              window.location.reload();
              fail();
            });
        } else {
          fail(new Error("Session Expired"));
        }
      } else {
        // console.log("Session ok");
        success();
      }
    });

  const ajaxSession = (path, options) =>
    new Promise((success, fail) => {
      checkOffline()
        .then((offline) => {
          if (!offline) {
            // console.log("online");
            R.pipeP(checkAccessToken, () => {
              // console.log("Sending request");
              return ajax(path, options);
            })()
              .then(success)
              .catch(fail);
          } else {
            // console.log("offline");
            fail("offline");
          }
        })
        .catch(() => {
          // console.log("offline");
          fail("offline");
        });
    });

  const getSessionApiUrl = (path, optional_params) =>
    new Promise((success, fail) => {
      checkOffline()
        .then((offline) => {
          if (!offline) {
            // console.log("online");
            R.pipeP(checkAccessToken, () => {
              // console.log("Getting API url");
              return Promise.resolve(getApiUrl(path, optional_params));
            })()
              .then(success)
              .catch(fail);
          } else {
            // console.log("offline");
            fail("offline");
          }
        })
        .catch(() => {
          // console.log("offline");
          fail("offline");
        });
    });

  const getUserSessionUserData = () => sessionHandler.userData;
  const setUserSessionUserData = (user_data) =>
    (sessionHandler.userData = user_data);
  const getUserSessionForceLocalSync = () => sessionHandler.forceLocalSync;
  const setUserSessionForceLocalSync = (forceLocalSync) =>
    (sessionHandler.forceLocalSync = forceLocalSync);

  const checkOffline = () =>
    new Promise((success) => {
      // console.log("Checking Offline");
      if (sessionHandler.checkOfflineOn) {
        const statusNow = moment();

        if (
          sessionHandler.statusLastCallTime === 0 ||
          statusNow.diff(sessionHandler.statusLastCallTime) > 60 * 1000
        ) {
          // console.log("Checking Status");
          const statusUrl = sessionHandler.accessToken
            ? globalOptions.appUrl +
              "app/status?access_token=" +
              encodeURIComponent(sessionHandler.accessToken.access_token)
            : globalOptions.appUrl + "app/status";
          Twix.ajax({
            url: statusUrl,
            success: ({ status }) => {
              sessionHandler.lastStatus = status !== "online";
              sessionHandler.statusLastCallTime = moment();
              // console.log("Server Offline", sessionHandler.lastStatus);
              apiOptions.apiActions.connectionStatus({
                connected: !sessionHandler.lastStatus,
                state: "online",
                error: "",
              });
              success(sessionHandler.lastStatus);
            },
            error: (statusCode) => {
              if (statusCode === 401 && sessionHandler.accessToken) {
                localStorage.removeItem("access_token");
                localStorage.removeItem("refresh_token");
                window.location.reload();
                return;
              }
              // console.log("NO CONNECTION OR ERROR", statusCode);
              sessionHandler.lastStatus = true;
              sessionHandler.statusLastCallTime = moment();
              // console.log(
              //   `Status error ${error} / Offline`,
              //   sessionHandler.lastStatus
              // );
              apiOptions.apiActions.connectionStatus({
                connected: !sessionHandler.lastStatus,
                state: "server error",
                error: statusCode,
              });
              success(sessionHandler.lastStatus);
            },
          });
        } else {
          // console.log("Cached offline", sessionHandler.lastStatus);
          apiOptions.apiActions.connectionStatus({
            connected: !sessionHandler.lastStatus,
            offline: "cached",
            error: "",
          });
          success(sessionHandler.lastStatus);
        }
      } else {
        // console.log("CheckofflineOn off");
        apiOptions.apiActions.connectionStatus({
          connected: !sessionHandler.lastStatus,
          offline: "offline mode",
          error: "",
        });
        success(true);
      }
    });

  const setApiActions = (actions) =>
    (apiOptions.apiActions = R.merge(apiOptions.apiActions, actions));

  /**
   * The current access token, refreshed first if it has expired.
   *
   * Every call made through ajaxSession already does this; anything that talks
   * to the API on its own (the support chat streams its own fetch) has to ask
   * for the token this way, or it sends whichever one it was holding — which
   * another tab may have rotated out from under it.
   */
  const ensureAccessToken = () =>
    checkAccessToken().then(
      () => sessionHandler.accessToken?.access_token || null,
    );

  return {
    addFixedParam,
    addFixedData,
    ajax,
    ajaxSession,
    ensureAccessToken,
    checkOffline,
    setAccessToken,
    getUserSessionUserData,
    setUserSessionUserData,
    getUserSessionForceLocalSync,
    setUserSessionForceLocalSync,
    getSessionApiUrl,
    setApiActions,
  };
};

let API_CONFIG_KEY =
  process.env.API_CONFIG_KEY || process.env.GATSBY_API_CONFIG_KEY;
if (window) {
  if (window.location.hostname === "localhost") {
    API_CONFIG_KEY = "local";
  } else {
    API_CONFIG_KEY = window.location.hostname;
  }
}

// console.log("API_CONFIG_KEY:", API_CONFIG_KEY);

export const apiConfig = initConfig(API_CONFIG_KEY);
export const api = apiInit(apiConfig.apiServer)({ appUrl: apiConfig.appUrl });
